import React, { FunctionComponent, useState } from "react";
import { connect, MapDispatchToProps, MapStateToProps, MergeProps } from "react-redux";

import { MessageWindow, Text } from "@multiweb/reactComponents";
import { bridge } from "@multiweb/reactCore/bridge";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { EMPTY_OBJECT } from "@multiweb/reactUtils";
import { IAppState } from "@multiweb/types/react";
import { createUpdateDefinitionParticipants } from "../../../actions";
import { ParticipantType } from "../../../enums";
import { LOC_CANCEL, LOC_DEFINITION_PARTICIPANT, LOC_DELETE, LOC_DELETE_SELECTED_PARTICIPANT, LOC_NEW_PARTICIPANT, LOC_NO_DEFINITION_PARTICIPANT_CREATED } from "../../../locKeys";
import { getDefinitionParticipants, isEditDialogHidden } from "../../../selectors";
import { IActivity, IParticipant, WorkflowEditorProps } from "../../../types";
import { UiWorkflow } from "../../../UiWorkflow";
import { getUiWorkflowMutableObject } from "../../../utils";
import { DefinitionParticipant } from "../../../workflowModel/Participants";
import { SideListFrame, SidePanelItemsSource } from "../components";
import { EditDialog } from "../EditDialog";
import { DefinitionParticipantsEditPage } from "./tabs/pages";

const { generateUUID } = bridge;

type DefinitionParticipantsEditDialogComponentProps = {
    readonly isHidden: boolean;
    readonly definitionParticipants: readonly IParticipant[];
    readonly uiWorkflow: UiWorkflow;
    readonly updateDefinitionParticipants: (newDefinitionParticipants: readonly IParticipant[], newActivites?: readonly IActivity[]) => void;
    readonly onCloseEdit: () => void;
    readonly noDefinitionParticipantCreatedText: string;
    readonly deleteSelectedParticipantText: string;
    readonly newParticipantText: string;
} & WorkflowEditorProps;

const DefinitionParticipantsEditDialogComponent: FunctionComponent<DefinitionParticipantsEditDialogComponentProps> = ({ hostKey, isHidden, definitionParticipants, uiWorkflow, updateDefinitionParticipants, onCloseEdit, noDefinitionParticipantCreatedText, deleteSelectedParticipantText, newParticipantText }) => {
    const [selectedParticipantIndex, setSelectedParticipantIndex] = useState(0);
    const [isMessageWindowShown, setIsMessageWindowShown] = useState(false);
    const [touched, setTouched] = useState(false);
    const selectedParticipant = definitionParticipants[selectedParticipantIndex];

    const actionsItemsSource: readonly SidePanelItemsSource[] = definitionParticipants.map(({ name, properties }, index) => {
        return { headerName: name == "" ? newParticipantText : name, description1: properties["roleName"], description2: properties["condition"], onItemClick: () => setSelectedParticipantIndex(index) };
    });

    const handleUpdateDefinitionParticipants = (newParticipants: readonly IParticipant[]) => {
        updateDefinitionParticipants(newParticipants);
        !touched && setTouched(true);
    };

    const onCloseMessageWindow = () => {
        setIsMessageWindowShown(false);
    };

    const addParticipant = () => {
        const newParticipants = [...definitionParticipants, {
            name: "",
            identifier: generateUUID(),
            locKey: LOC_DEFINITION_PARTICIPANT,
            participantType: ParticipantType.DefinitionParticipant,
            properties: EMPTY_OBJECT,
        }];
        handleUpdateDefinitionParticipants(newParticipants);
        setSelectedParticipantIndex(newParticipants.length - 1);
    };

    const removeParticipant = () => {
        const newParticipants = definitionParticipants.filter((_participant, actionIndex) => actionIndex !== selectedParticipantIndex);
        handleUpdateDefinitionParticipants(newParticipants);
        setSelectedParticipantIndex(selectedParticipantIndex - 1 >= 0 ? selectedParticipantIndex - 1 : 0);
    };

    const copyParticipant = () => {
        const newParticipants = [...definitionParticipants];
        newParticipants.splice(selectedParticipantIndex + 1, 0, { ...selectedParticipant, identifier: generateUUID() });
        handleUpdateDefinitionParticipants(newParticipants);
        setSelectedParticipantIndex(selectedParticipantIndex + 1);
    };

    const updateDefParticipant = (participant: IParticipant, propertyName: string, newValue: string) => {
        const newParticipants = [...definitionParticipants];
        const newProperties = { ...participant.properties };
        newProperties[propertyName] = newValue;
        const newParticipant = { ...participant, properties: newProperties };
        newParticipants[selectedParticipantIndex] = newParticipant;
        handleUpdateDefinitionParticipants(newParticipants);
    };

    const onSaveClick = async () => {
        const defParticipantsToDelete = uiWorkflow.workflow.definitionParticipants.filter(wfDefParticipant =>
            definitionParticipants.every(defParticipant => defParticipant.identifier !== wfDefParticipant.identifier),
        );

        defParticipantsToDelete.forEach(participantToDelete => {
            uiWorkflow.workflow.removeDefinitionParticipant(participantToDelete.identifier);
        });

        definitionParticipants.forEach(defPartip => {
            const oldPartip = uiWorkflow.workflow.definitionParticipants.find(wfDefPartip => wfDefPartip.identifier === defPartip.identifier);
            if (oldPartip) {
                oldPartip.condition = defPartip.properties["condition"] ?? "";
                oldPartip.roleName = defPartip.properties["roleName"] ?? "";
                oldPartip.name = defPartip.properties["name"];
            } else {
                uiWorkflow.workflow.definitionParticipants.push(new DefinitionParticipant(
                    defPartip.identifier,
                    defPartip.properties["name"],
                    defPartip.properties["condition"] ?? "",
                    defPartip.properties["roleName"] ?? ""));
            }
        });

        // Musím aktualizovat hlavně aktivity a definiční hráče, aby se jim pozměnil identifier na novou hodnotu.
        const { activities, definitionParticipants: oldDefParticipants } = uiWorkflow.workflowModel;
        updateDefinitionParticipants(oldDefParticipants, activities);
    };

    const onCancelClick = () => {
        handleUpdateDefinitionParticipants(uiWorkflow.workflowModel.definitionParticipants);
    };

    const canSaveDefinitionParticipants = () => {
        const hasEveryParticipantName = definitionParticipants.every(({ name }) => name != null && name.length > 0);
        const areParticipantsNamesUnique = () => {
            const participantsNames = definitionParticipants.map(part => part.name);
            const distinctNames = [...new Set(participantsNames)];
            return participantsNames.length === distinctNames.length;
        };

        const noParticipantsNameIsSystemOrOwner = () => definitionParticipants.every(({ name }) => name !== ParticipantType.System && name !== ParticipantType.Owner);

        return hasEveryParticipantName && areParticipantsNamesUnique() && noParticipantsNameIsSystemOrOwner();
    };

    const handleDeleteButtonClick = () => {
        setIsMessageWindowShown(true);
    };

    return (
        <EditDialog width={800} height={600} canSave={canSaveDefinitionParticipants()} {...{ hostKey, isHidden, touched, onCloseEdit, onSaveClick, onCancelClick, setTouched }}>
            <SideListFrame
                hostKey={hostKey}
                selectedItemIndex={selectedParticipantIndex}
                itemsSource={actionsItemsSource}
                onAddItemClick={addParticipant}
                onRemoveItemClick={handleDeleteButtonClick}
                onCopyItemClick={copyParticipant}>
                {selectedParticipant ?
                    <DefinitionParticipantsEditPage {...{ hostKey, selectedParticipant, updateDefParticipant }} /> :
                    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Text value={noDefinitionParticipantCreatedText} />
                    </div>
                }
            </SideListFrame>
            {isMessageWindowShown &&
                <MessageWindow
                    primaryButtonLocKey={LOC_DELETE}
                    secondaryButtonLocKey={LOC_CANCEL}
                    onPrimaryButtonClick={removeParticipant}
                    {...{ onCloseMessageWindow }}
                >
                    {deleteSelectedParticipantText}
                </MessageWindow>}
        </EditDialog>
    );
};

type DefinitionParticipantsEditDialogProps = Pick<DefinitionParticipantsEditDialogComponentProps, "hostKey" | "onCloseEdit">;
type StateProps = Pick<DefinitionParticipantsEditDialogComponentProps, "isHidden" | "definitionParticipants" | "noDefinitionParticipantCreatedText" | "deleteSelectedParticipantText" | "newParticipantText">;

const mapStateToProps: MapStateToProps<StateProps, DefinitionParticipantsEditDialogProps, IAppState> = (state, { hostKey }) => ({
    isHidden: isEditDialogHidden(state, hostKey),
    definitionParticipants: getDefinitionParticipants(state, hostKey),
    noDefinitionParticipantCreatedText: getPrimaryText(state, LOC_NO_DEFINITION_PARTICIPANT_CREATED) ?? "Žádný definiční hráč nebyl zatím vytvořen...",
    deleteSelectedParticipantText: getPrimaryText(state, LOC_DELETE_SELECTED_PARTICIPANT) ?? "Opravdu chcete smazat vybraného hráče?",
    newParticipantText: getPrimaryText(state, LOC_NEW_PARTICIPANT) ?? "Nový hráč",
});

type DispatchProps = Pick<DefinitionParticipantsEditDialogComponentProps, "updateDefinitionParticipants">;

const mapDispatchToProps: MapDispatchToProps<DispatchProps, DefinitionParticipantsEditDialogProps> = (dispatch, { hostKey }) => ({
    updateDefinitionParticipants: (newParticipants, newActivities) => dispatch(createUpdateDefinitionParticipants(hostKey, newParticipants, newActivities)),
});

const mergeProps: MergeProps<StateProps, DispatchProps, DefinitionParticipantsEditDialogProps, DefinitionParticipantsEditDialogComponentProps> = (
    stateProps,
    dispatchProps,
    ownProps,
) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    uiWorkflow: getUiWorkflowMutableObject(ownProps.hostKey),
});

export const DefinitionParticipantsEditDialog = connect(mapStateToProps, mapDispatchToProps, mergeProps)(DefinitionParticipantsEditDialogComponent);
